/**
 * UserSettingsContext — персональные настройки пользователя (users.settings).
 *
 * Настройки приходят с сервера при входе (auth/check -> user.settings) и
 * доступны всем модулям. Изменения (язык, фильтры разделов) сразу применяются
 * локально и сохраняются на сервере; при следующем входе они становятся
 * значениями по умолчанию (сводка/расписание открываются на выбранном филиале,
 * интерфейс — на выбранном языке).
 *
 * Использование:
 *   const { get, update } = useUserSettings();
 *   const branch = get("filters.schedule.clinic_id", "");
 *   update({ filters: { schedule: { clinic_id: value } } }); // merge на сервере
 */
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useTranslation } from "react-i18next";

import GetData from "../services/getData";
import { normalizeLanguage } from "../i18n/config";

const UserSettingsContext = createContext(null);

/** Заглушка, если хук вызван вне провайдера (например, на экране входа). */
const FALLBACK = {
    settings: {},
    get: (_path, fallback = "") => fallback,
    update: () => {},
};

/** Рекурсивно сливает patch в base (объекты объединяются, прочее перезаписывается). */
function deepMerge(base, patch) {
    const result = { ...(base || {}) };
    Object.entries(patch || {}).forEach(([key, value]) => {
        if (
            value &&
            typeof value === "object" &&
            !Array.isArray(value) &&
            result[key] &&
            typeof result[key] === "object" &&
            !Array.isArray(result[key])
        ) {
            result[key] = deepMerge(result[key], value);
        } else {
            result[key] = value;
        }
    });
    return result;
}

export function UserSettingsProvider({ settings: initialSettings, children }) {
    const { i18n } = useTranslation();
    const [settings, setSettings] = useState(() => initialSettings || {});
    const settingsRef = useRef(settings);

    // Применяем сохранённый язык как значение по умолчанию один раз при старте.
    useEffect(() => {
        const saved = initialSettings?.language;
        if (!saved) return;
        const next = normalizeLanguage(saved);
        const current = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
        if (next !== current) {
            i18n.changeLanguage(next);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /** Значение по точечному пути ("filters.schedule.clinic_id"). */
    const get = useCallback((path, fallback = "") => {
        let node = settingsRef.current;
        for (const key of String(path).split(".")) {
            if (node == null || typeof node !== "object") return fallback;
            node = node[key];
        }
        return node == null ? fallback : node;
    }, []);

    /** Оптимистично применяет patch локально и сохраняет его на сервере (merge). */
    const update = useCallback((patch) => {
        if (!patch || typeof patch !== "object") return;
        settingsRef.current = deepMerge(settingsRef.current, patch);
        setSettings(settingsRef.current);

        GetData({
            module: "settings",
            block: "profile",
            action: "save",
            settings: JSON.stringify(patch),
        }).catch((err) => {
            console.error("Не удалось сохранить настройки пользователя:", err);
        });
    }, []);

    const value = useMemo(
        () => ({ settings, get, update }),
        [settings, get, update],
    );

    return (
        <UserSettingsContext.Provider value={value}>
            {children}
        </UserSettingsContext.Provider>
    );
}

export function useUserSettings() {
    return useContext(UserSettingsContext) || FALLBACK;
}

export default UserSettingsProvider;
